import { Box, Button, NumberInput, Select, Text, Textarea, TextInput } from "@mantine/core";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnsApi } from "../../apis/returns";
import { currencyFormat } from "../../utils";
import { handleError } from "../../utils/error-handler";
import { message } from "../../utils/Message";
import classes from "./return-decision.module.css";

type Props = {
  returnRequest: any;
  /** Маргаанаас хойш дахин санал гаргаж буй эсэх. */
  isRepropose: boolean;
  /** Footer-ын «Хүсэлтээс татгалзах» товчийг drawer өөрөө барина. */
  onReject: () => void;
  onSent: () => void;
  /** Маягийн дээр, нэг гүйлгэлтэнд орох агуулга (хүсэлтийн карт, маргаан). */
  lead?: ReactNode;
};

const RESOLUTIONS = ["REFUND_MONEY", "REPLACEMENT", "NEXT_ORDER_DISCOUNT"] as const;
// propose.ts NOT_REQUIRED-ийг татгалздаг (select_transport_liability) тул 4 бодит арга.
const SHIPPINGS = ["SELLER_PICKS_UP", "BUYER_DELIVERS", "BUYER_SHIPS", "SELLER_PAYS_SHIPPING"] as const;
const PRESETS = [100, 75, 50] as const;

const money = (v: number) => currencyFormat(v);

export function ReturnProposalForm({ returnRequest: rr, isRepropose, onReject, onSent, lead }: Props) {
  const { t } = useTranslation("returns");
  const total = Number(rr.totalRequestedAmount || 0);

  const [resolution, setResolution] = useState<(typeof RESOLUTIONS)[number]>("REFUND_MONEY");
  const [percent, setPercent] = useState<number>(100);
  const [customPct, setCustomPct] = useState(false);
  const [deductionReason, setDeductionReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<"BANK_TRANSFER" | "INVOICE_DEDUCTION">("BANK_TRANSFER");
  const [shipping, setShipping] = useState<(typeof SHIPPINGS)[number]>("SELLER_PICKS_UP");
  const [warehouseId, setWarehouseId] = useState<string | null>(rr.targetWarehouseId ?? null);
  const [warehouseReason, setWarehouseReason] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  // README §7.6.13 — every resolution that brings goods back needs a receiving
  // warehouse, whoever ships them; the server refuses (RET-046) without one.
  const needsWarehouse = resolution !== "NEXT_ORDER_DISCOUNT";
  const { data: wh } = useSWR(needsWarehouse ? "returns.warehouses" : null, () => ReturnsApi.warehouses(), {
    revalidateOnFocus: false,
  });
  const warehouses: { id: string; name: string; code?: string | null }[] = wh?.warehouses ?? [];

  const refundAmount = Math.round(total * percent) / 100;
  const deductionAmount = total - refundAmount;
  const warehouseChanged = needsWarehouse && !!warehouseId && warehouseId !== (rr.targetWarehouseId ?? null);
  // A reason is asked only when an existing target is replaced, not when the first one is set.
  const reasonNeeded = warehouseChanged && !!rr.targetWarehouseId;
  const hintKey = shipping in { SELLER_PICKS_UP: 1, BUYER_DELIVERS: 1, BUYER_SHIPS: 1, SELLER_PAYS_SHIPPING: 1 } ? shipping : "none";

  const send = async () => {
    if (!(percent >= 0 && percent <= 100)) return message.error(t("decision.errPercent"));
    if (percent < 100 && !deductionReason.trim()) return message.error(t("decision.errDeduction"));
    if (reasonNeeded && !warehouseReason.trim()) return message.error(t("decision.errWarehouseReason"));
    try {
      setSending(true);
      await ReturnsApi.propose(rr.id, {
        resolutionType: resolution,
        refundPercent: percent,
        deductionReason: deductionReason.trim() || undefined,
        refundMethod: resolution === "REFUND_MONEY" ? refundMethod : undefined,
        shippingResponsibility: shipping,
        proposalNotes: notes.trim() || undefined,
        ...(warehouseChanged
          ? { targetWarehouseId: warehouseId, ...(reasonNeeded ? { targetWarehouseChangeReason: warehouseReason.trim() } : {}) }
          : {}),
        expectedVersion: rr.version,
      });
      message.success(t("decision.sent"));
      onSent();
    } catch (error) {
      handleError(error, t("decision.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Box className={classes.body}>
        {lead}
        <Box component="section" className={classes.section}>
          <Text component="h2" className={classes.sectionTitle}>{t(isRepropose ? "decision.reProposal" : "decision.proposal")}</Text>

          <Box className={classes.field}>
            <Text span className={classes.fieldLabel}>{t("decision.resolution")}</Text>
            <Box role="radiogroup" aria-label={t("decision.resolution")} className={classes.choiceGrid}>
              {RESOLUTIONS.map((r) => (
                <Box
                  key={r}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={resolution === r}
                  data-active={resolution === r || undefined}
                  className={classes.choice}
                  onClick={() => setResolution(r)}
                >
                  <Text span className={classes.choiceTitle}>{t(`decision.res.${r}.t`)}</Text>
                  <Text span className={classes.choiceDesc}>{t(`decision.res.${r}.d`)}</Text>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className={classes.twoCol}>
            <Box className={classes.field}>
              <Text span className={classes.fieldLabel}>{t("decision.refundAmount")}</Text>
              <Box className={classes.amountBox}>
                {money(refundAmount).replace(/\s*₮$/, "")}
                <Text span className={classes.amountCur}>₮</Text>
              </Box>
              <Box className={classes.chips}>
                {PRESETS.map((p) => (
                  <Box
                    key={p}
                    component="button"
                    type="button"
                    className={classes.chip}
                    data-active={(!customPct && percent === p) || undefined}
                    onClick={() => { setCustomPct(false); setPercent(p); }}
                  >
                    {p}%
                  </Box>
                ))}
                <Box component="button" type="button" className={classes.chip} data-active={customPct || undefined} onClick={() => setCustomPct(true)}>
                  {t("decision.pctOther")}
                </Box>
              </Box>
              {customPct && (
                <NumberInput
                  aria-label={t("decision.pctInput")}
                  value={percent}
                  onChange={(v) => setPercent(Number(v) || 0)}
                  min={0}
                  max={100}
                  suffix="%"
                  size="xs"
                  hideControls
                />
              )}
            </Box>

            {resolution === "REFUND_MONEY" && (
              <Box className={classes.field}>
                <Text span className={classes.fieldLabel}>{t("decision.refundMethod")}</Text>
                <Select
                  aria-label={t("decision.refundMethod")}
                  allowDeselect={false}
                  value={refundMethod}
                  onChange={(v) => v && setRefundMethod(v as typeof refundMethod)}
                  data={[
                    { value: "BANK_TRANSFER", label: t("decision.methodBank") },
                    { value: "INVOICE_DEDUCTION", label: t("decision.methodInvoice"), disabled: !rr.invoiceId },
                  ]}
                />
                <Text span className={classes.hintText}>
                  {t("decision.deduction", { amount: money(deductionAmount), pct: Math.round((100 - percent) * 100) / 100 })}
                </Text>
              </Box>
            )}
          </Box>

          {percent < 100 && (
            <Box className={classes.field}>
              <Text span className={classes.fieldLabel}>{t("decision.deductionReason")}</Text>
              <TextInput value={deductionReason} onChange={(e) => setDeductionReason(e.currentTarget.value)} placeholder={t("decision.deductionReasonPh")} />
            </Box>
          )}

          <Box className={classes.field}>
            <Text span className={classes.fieldLabel}>{t("decision.goods")}</Text>
            <Box role="radiogroup" aria-label={t("decision.goods")} className={classes.choiceRow}>
              {SHIPPINGS.map((s) => (
                <Box
                  key={s}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={shipping === s}
                  data-active={shipping === s || undefined}
                  className={`${classes.choice} ${classes.choiceCompact}`}
                  onClick={() => setShipping(s)}
                >
                  {t(`decision.ship.${s}`)}
                </Box>
              ))}
            </Box>
          </Box>

          {needsWarehouse && (
            <Box className={classes.field}>
              <Text span className={classes.fieldLabel}>{t("decision.warehouse")}</Text>
              <Select
                aria-label={t("decision.warehouse")}
                placeholder={t("decision.warehousePh")}
                searchable
                value={warehouseId}
                onChange={setWarehouseId}
                data={warehouses.map((w) => ({ value: w.id, label: w.code ? `${w.name} (${w.code})` : w.name }))}
              />
              {reasonNeeded && (
                <TextInput value={warehouseReason} onChange={(e) => setWarehouseReason(e.currentTarget.value)} placeholder={t("decision.warehouseReason")} aria-label={t("decision.warehouseReason")} />
              )}
            </Box>
          )}

          <Box className={classes.field}>
            <Text span className={classes.fieldLabel}>{t("decision.notes")}</Text>
            <Textarea value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder={t("decision.notesPh")} minRows={2} autosize />
          </Box>
        </Box>
      </Box>

      <Box className={classes.foot}>
        <Button variant="subtle" color="red" onClick={onReject}>{t("decision.reject")}</Button>
        <Box className={classes.footRight}>
          <Text span className={classes.footHint}>{t(`decision.hint.${hintKey}`)}</Text>
          <Button color="brand" loading={sending} onClick={send}>{t("decision.send")}</Button>
        </Box>
      </Box>
    </>
  );
}
